import { useReporting } from '@/hooks/useReporting';
import { useRouteMetadata } from '@/hooks/useRouteMetadata';
import { ProviderType } from '@/reporting/types';
import logger from '@/utils/client-logger';
import { useCallback } from 'react';

interface FormValidStatus {
  card_number: string;
  expiry_date: string;
  cvv: string;
}

/**
 * 支付中台埋点追踪 Hook
 * 封装支付中台相关的神策埋点事件
 */
export function usePayCenterTrack() {
  const { trackEvent } = useReporting();
  const { cnName } = useRouteMetadata();

  // 获取基础参数
  const getBaseParams = useCallback(() => {
    const pageName = cnName || '其他';
    const urlPath = typeof window !== 'undefined' ? window.location.href : '';

    return {
      page_name: pageName,
      url_path: urlPath,
      popup_type: '信用卡支付弹窗',
    };
  }, [cnName]);

  /**
   * @TrackType sensorsdata
   * @EventType CardPopUp_Exp
   * @EventDesc 支付中台弹窗曝光
   * @EventParams {
   * page_name	页面名称
   * url_path	页面路径
   * popup_type 弹窗类型
   * }
   * @Tapd 支付中台
   */
  const trackCardPopUpExp = useCallback(() => {
    if (typeof window === 'undefined') return;

    try {
      const baseParams = getBaseParams();

      trackEvent(
        {
          eventName: 'CardPopUp_Exp',
          properties: baseParams,
        },
        [ProviderType.SENSORS],
      );

      logger.log('🔍 神策埋点 - CardPopUp_Exp:', baseParams);
    } catch (error) {
      logger.error('CardPopUp_Exp tracking error:', error);
    }
  }, [trackEvent, getBaseParams]);

  /**
   * @TrackType sensorsdata
   * @EventType CardPopUpCloseBtn_Click
   * @EventDesc 信用卡支付弹窗关闭点击
   * @EventParams {
   * page_name	页面名称
   * url_path	页面路径
   * popup_type 弹窗类型
   * card_number_error_reason	卡号输入框报错原因
   * expiry_date_error_reason	有效期输入框报错原因
   * security_code_error_reason	安全码输入框报错原因
   * card_pay_fail_reason	信用卡支付失败原因
   * }
   * @Tapd 支付中台
   */
  const trackCardPopUpCloseBtnClick = useCallback(
    (formValidStatus?: FormValidStatus) => {
      if (typeof window === 'undefined') return;

      try {
        const baseParams = getBaseParams();
        const params = {
          ...baseParams,
          card_number_error_reason: formValidStatus?.card_number || '',
          expiry_date_error_reason: formValidStatus?.expiry_date || '',
          security_code_error_reason: formValidStatus?.cvv || '',
          card_pay_fail_reason: '',
        };

        trackEvent(
          {
            eventName: 'CardPopUpCloseBtn_Click',
            properties: params,
          },
          [ProviderType.SENSORS],
        );

        logger.log('🔍 神策埋点 - CardPopUpCloseBtn_Click:', params);
      } catch (error) {
        logger.error('CardPopUpCloseBtn_Click tracking error:', error);
      }
    },
    [trackEvent, getBaseParams],
  );

  /**
   * @TrackType sensorsdata
   * @EventType CardPopUpPayBtn_Click
   * @EventDesc 信用卡支付弹窗支付点击
   * @EventParams {
   * page_name	页面名称
   * url_path	页面路径
   * popup_type 弹窗类型
   * card_number_error_reason	卡号输入框报错原因
   * expiry_date_error_reason	有效期输入框报错原因
   * security_code_error_reason	安全码输入框报错原因
   * }
   * @Tapd 支付中台
   */
  const trackCardPopUpPayBtnClick = useCallback(
    (formValidStatus?: FormValidStatus) => {
      if (typeof window === 'undefined') return;

      try {
        const baseParams = getBaseParams();
        const params = {
          ...baseParams,
          card_number_error_reason: formValidStatus?.card_number || '',
          expiry_date_error_reason: formValidStatus?.expiry_date || '',
          security_code_error_reason: formValidStatus?.cvv || '',
        };

        trackEvent(
          {
            eventName: 'CardPopUpPayBtn_Click',
            properties: params,
          },
          [ProviderType.SENSORS],
        );

        logger.log('🔍 神策埋点 - CardPopUpPayBtn_Click:', params);
      } catch (error) {
        logger.error('CardPopUpPayBtn_Click tracking error:', error);
      }
    },
    [trackEvent, getBaseParams],
  );

  /**
   * @TrackType sensorsdata
   * @EventType CardPopUpPay_Result
   * @EventDesc 信用卡支付pay点击结果
   * @EventParams {
   * page_name	页面名称
   * url_path	页面路径
   * popup_type 弹窗类型
   * card_is_pay_success	信用卡是否支付成功
   * card_pay_fail_reason	信用卡支付失败原因
   * }
   * @Tapd 支付中台
   */
  const trackCardPopUpPayResult = useCallback(
    (cardIsPaySuccess: boolean, cardPayFailReason?: string) => {
      if (typeof window === 'undefined') return;

      try {
        const baseParams = getBaseParams();
        const params = {
          ...baseParams,
          card_is_pay_success: cardIsPaySuccess,
          card_pay_fail_reason: cardPayFailReason || '',
        };

        trackEvent(
          {
            eventName: 'CardPopUpPay_Result',
            properties: params,
          },
          [ProviderType.SENSORS],
        );

        logger.log('🔍 神策埋点 - CardPopUpPay_Result:', params);
      } catch (error) {
        logger.error('CardPopUpPay_Result tracking error:', error);
      }
    },
    [trackEvent, getBaseParams],
  );

  return {
    trackCardPopUpExp,
    trackCardPopUpCloseBtnClick,
    trackCardPopUpPayBtnClick,
    trackCardPopUpPayResult,
  };
}

export type { FormValidStatus };
